import { useEffect } from "react";
import axios from "axios";
import Head from "next/head";
import { parse } from "node-html-parser";
import Collapsible from "react-collapsible";
import classNames from "classnames";

import Layout from "../components/Layout";
import Button from "../components/Button";
import { useAuth } from "../hooks/useAuth";

import styles from "./help.module.css";
import { useRouter } from "next/router";
import { capitalize } from "../util";
import Link from "next/link";

const LANGUAGES = ["en", "es", "fr", "ar", "zh"];

function FaqTrigger({ title, open, forceOpen }) {
  return (
    <h4
      className={classNames(
        "px-5 transition-all duration-100",
        forceOpen || open ? "bg-gray-700 py-3 mb-3 rounded-t" : "py-3 rounded",
        !forceOpen && "hover:bg-gray-600 cursor-pointer"
      )}
    >
      {title}
    </h4>
  );
}

function FaqEntry({ title, body, strings, children, forceOpen }) {
  return (
    <Collapsible
      containerElementProps={{
        className: "rounded shadow-outline-gray mb-5"
      }}
      trigger={<FaqTrigger title={title} />}
      triggerWhenOpen={<FaqTrigger title={title} open forceOpen={forceOpen} />}
      easing="ease-in-out"
      transitionTime={300}
      open={forceOpen}
      handleTriggerClick={forceOpen ? () => {} : undefined}
    >
      <div className="px-5 pt-3 pb-3">
        {body ? <div dangerouslySetInnerHTML={{ __html: body }} /> : undefined}
        {children}
        {!forceOpen ? (
          <div className="text-sm text-gray-300 bg-gray-700 mt-3 -mb-3 py-3 -mx-5 px-5 rounded-b">
            {strings.need_more_help}{" "}
            <a
              className="cursor-pointer"
              onClick={e => {
                document.querySelector(".live-chat-button").click();
              }}
            >
              {capitalize(strings.launch_support)} »
            </a>
          </div>
        ) : null}
      </div>
    </Collapsible>
  );
}

export default function Faq({ data }) {
  const router = useRouter();
  const { user, singleOrganization } = useAuth();
  const lang = router.query.lang || router.locale || "en";
  const content = data[lang] || data["en"];

  useEffect(() => {
    window.Intercom("boot", {
      app_id: APP_ID,
      hide_default_launcher: true,
      background_color: "#5850ec",
      action_color: "#5850ec"
    });
  }, []);

  useEffect(() => {
    if (router.query.chat) {
      window.Intercom("showNewMessage");
    }
  }, [router.query.chat]);

  useEffect(() => {
    if (singleOrganization?.use_aarp_registration_flow) {
      // Custom AARP device testing video + destinations to keep on custom site
      [...document.querySelectorAll("a")].forEach(el => {
        if (
          el.href ===
          "https://watch.eventive.org/account/play/5f0368a74282a70029055ca8"
        ) {
          el.href =
            "https://watch.aarp.org/aarptestvideo/play/6318de909df21400cd2defe6";
        } else if (el.hostname === "watch.eventive.org") {
          el.hostname = "watch.aarp.org";
        }
        el.innerText = el.innerText.replace(
          "watch.eventive.org",
          "watch.aarp.org"
        );
      });
    }
  }, [singleOrganization]);

  return (
    <Layout
      title={`${content.title} 🍿`}
      centered
      headerBackLink={router.query.back}
    >
      <Head>
        <title>Help | Eventive Virtual</title>
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `window.APP_ID = "oj5vkp3b";
          (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/' + APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
          `
          }}
        />
      </Head>
      <div className={styles.faq} dir={content.direction}>
        <div className="mb-6 text-center text-gray-300">
          {content.intro}{" "}
          <a
            className="cursor-pointer"
            onClick={e => {
              document.querySelector(".live-chat-button").click();
            }}
          >
            {content.launch_support_long || content.launch_support}
          </a>
          {lang === "zh" ? "。" : "."}
        </div>
        <nav>
          <bdo
            className="flex justify-center flex-wrap mx-4 items-center -mt-2 mb-6"
            dir="ltr"
          >
            {LANGUAGES.map((code, i) => (
              <Link
                href={`/help${code === "en" ? "" : `?lang=${code}`}`}
                shallow
              >
                <button
                  className={classNames(
                    "px-3 py-2 font-medium text-sm leading-5 rounded-md no-underline m-2",
                    code === lang
                      ? "text-gray-700 bg-gray-100 focus:outline-none focus:bg-gray-200 border border-gray-100"
                      : "text-gray-300 hover:text-gray-400 focus:outline-none focus:text-gray-700 focus:bg-gray-100 border border-gray-500"
                  )}
                >
                  {data[code].language}
                </button>
              </Link>
            ))}
          </bdo>
        </nav>
        <div>
          {content.sections.map(({ title, body }) =>
            // AARP mode: Exclude any mention of payment info since all free
            singleOrganization?.use_aarp_registration_flow &&
            title?.indexOf("payment information") !== -1 ? null : (
              // Otherwise, just render the normal FAQ
              <FaqEntry
                key={title}
                title={title}
                body={body}
                strings={{
                  need_more_help: content.need_more_help,
                  launch_support: content.launch_support
                }}
              />
            )
          )}
        </div>
        <FaqEntry title={content.still_need_help} forceOpen>
          <p>{content.still_need_help_detail}</p>
          <div className="text-center my-4 sm:justify-center sm:flex items-baseline">
            <Button size="huge" className="live-chat-button">
              {capitalize(content.launch_support)}
            </Button>
          </div>
        </FaqEntry>
      </div>
    </Layout>
  );
}

export async function getStaticProps(ctx) {
  const languages = {
    en: {
      language: "English",
      url: "https://help.eventive.org/en/articles/3922588-audience-faq",
      title: "We're here to help",
      intro:
        "Please browse our frequently asked questions below. If you need further assistance,",
      launch_support: "launch live chat support",
      still_need_help: "Still need help?",
      still_need_help_detail:
        "Don't worry, we're here to assist. Click the button below to enter our live chat support and we'll get you streaming as quickly as we can.",
      need_more_help: "Need more help?"
    },
    es: {
      language: "Español",
      url: "https://help.eventive.org/en/articles/4254079-faqs-en-espanol",
      title: "Estamos aquí para ayudar",
      intro:
        "Por favor, explore nuestro documento de respuestas a preguntas frecuentemente recibidas. Si necesita más ayuda,",
      launch_support: "abrir un chat vivo",
      still_need_help: "¿Todavía necesita ayuda?",
      still_need_help_detail:
        "No se preocupe, estamos aquí para ayudar. Haz clic abajo para abrir un chat vivo, y aseguramos que podrá ver su película tan pronto como posible.",
      need_more_help: "¿Necesita más ayuda?"
    },
    fr: {
      language: "Français",
      url: "https://help.eventive.org/en/articles/4523946-faq-en-francais",
      title: "Nous sommes là pour vous aider!",
      intro:
        "Veuillez parcourir nos réponses aux questions les plus fréquemment posées ci-dessous. Si vous avez besoin d’assistance supplémentaire,",
      launch_support_long:
        "lancez l’assistance par chat en direct (en anglais et en espagnol seulement)",
      launch_support: "Lancer l’assistance par chat en direct",
      still_need_help: "Encore besoin d’aide?",
      still_need_help_detail:
        "Ne vous inquiétez pas, nous sommes là pour vous aider. Cliquez sur le bouton ci-dessous pour accéder à notre assistance par chat en direct et nous vous ferons diffuser le plus rapidement possible.",
      need_more_help: "Besoin d'aide?"
    },
    ar: {
      language: "اَلْعَرَبِيَّةُ",
      url: "https://help.eventive.org/en/articles/4565328-arabic",
      title: "تُسعدنا مساعدتك",
      intro:
        "من فضلك ابحث عن استفسارك في صفحة الأسئلة المتكرّرة. وإذا كنت ما تزال بحاجةٍ للمساعدة؛ يمكنك التّحدّث معنا مباشرةً",
      launch_support_long: "يمكنك التّحدّث معنا مباشرةً",
      launch_support: "التحدّث مباشرةً مع خدمة العملاء",
      still_need_help: "وإذا لم تجد حلّاً لمشكلتك؛ لم تجد حلاً لمشكلتك؟",
      still_need_help_detail:
        "لا تقلق أبدًا. نحن دائماً في خدمتك، اضغط على الأيقونة في الأسفل للتحدّث مباشرةً مع خدمة العملاء، وستستمتع بمشاهدة كل ما تريده في أقرب فرصة.",
      need_more_help: "إذا لم تجد حلّاً لمشكلتك؛",
      direction: "rtl"
    },
    zh: {
      language: "中文",
      url: "https://help.eventive.org/en/articles/5311322-chinese-faq",
      title: "我們在此解答你的疑難",
      intro: "請參閱以下常見問題，如你需要進一步支援，",
      launch_support: "可開啟即時通訊查詢",
      still_need_help: "還有其他問題？",
      still_need_help_detail:
        "別擔心，我們在這裡為你提供幫助。點擊下面的按鈕進入我們的實時聊天服務，我們將盡快為你提供串流服務。",
      need_more_help: "需要更多支援？"
    }
  };

  let data = {};

  await Promise.all(
    Object.keys(languages).map(async code => {
      const { data: rawData } = await axios.get(languages[code].url);
      const rawArticle = parse(rawData).querySelector("article").innerHTML;
      const chunks = rawArticle
        .split(/<div class="intercom-interblocks-subheading/g)
        .filter(c => !!c);
      let sections = [];
      for (let c of chunks) {
        const chunkDom = parse(
          '<div class="intercom-interblocks-subheading' + c
        );
        const title = chunkDom.firstChild.text.trim();
        chunkDom.removeChild(chunkDom.firstChild);
        const body = chunkDom.toString();
        sections.push({ title, body });
      }
      data[code] = { ...languages[code], sections };
    })
  );

  return {
    props: { data }
  };
}
